import React, { useState, useRef, useCallback, useEffect } from "react";
import { observer } from "mobx-react";
import { TextField, Select, Button, SwitchMode } from "@ann/web-components";
import {
  FiltersContainerWrapper,
  FiltersColumn,
  SearchContainer,
  TableSwitcherColumn,
  TableSwitcherPlaceholder,
} from "./FiltersContainer.styled";

const FiltersContainer = observer(
  ({
    mode,
    setMode,
    loading,
    filters,
    filterHandlers,
    filterUI,
  }) => {
    const [localSearchValue, setLocalSearchValue] = useState(filters?.searchValue || "");
    const searchTimeoutRef = useRef(null);
  
    // Desestructurar valores de los objetos agrupados
    const {
      statusOptions,
      advisorOptions,
      vehicleOptions,
      lastUpdateOptions,
      filteredStatus,
      filteredAdvisor,
      filteredLastUpdate,
      filteredVehicle,
      selectedStatus,
      selectedAdvisor,
      selectedVehicle,
      selectedLastUpdatePreset,
      searchValue,
      hasActiveFilters,
    } = filters;

    const {
      handleStatusChange,
      handleAdvisorChange,
      handleVehicleChange,
      handleLastUpdatePresetChange,
      handleSearchChange,
      handleClearFiltersWithKeys,
    } = filterHandlers;

    const {
      filtersKey,
      updateLocalSearch,
    } = filterUI;

    const handleSearchChangeCallback = useCallback((value) => {
      setLocalSearchValue(value);
      
      // Clear existing timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      // Set new timeout for 500ms debounce
      searchTimeoutRef.current = setTimeout(() => {
        if (handleSearchChange) {
          handleSearchChange(value);
        }
      }, 500);
    }, [handleSearchChange]);

    // Cleanup timeout on unmount
    useEffect(() => {
      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }, []);

    // Sync local state with filters when it changes
    useEffect(() => {
      setLocalSearchValue(searchValue || "");
    }, [searchValue]);

    return (
      <FiltersContainerWrapper>
        <FiltersColumn>
          <SearchContainer>
            <TextField
              iconLeft="search"
              onChange={handleSearchChangeCallback}
              size="small"
              placeholder="Search"
              value={localSearchValue}
              disabled={loading}
            />
          </SearchContainer>
        
        <Select
          key={`status-filter-${filtersKey}`}
          label="Status"
          placeholder="Status"
          onChange={handleStatusChange}
          size="small"
          value={
            statusOptions.find((option) => option.value === selectedStatus) || undefined
          }
          width="auto"
          minWidth="103px"
          disabled={loading}
        >
          <Select.Menu width="160px">
            <Select.Search
              placeholder="Search"
              onChange={(value) => updateLocalSearch("status", value)}
              label=""
            />
            {filteredStatus.map((status) => (
              <Select.Item
                key={status.value}
                value={status.value}
                label={status.label}
              >
                {status.label}
              </Select.Item>
            ))}
          </Select.Menu>
        </Select>
        
        <Select
          key={`advisor-filter-${filtersKey}`}
          label="Advisor"
          placeholder="Advisor"
          onChange={handleAdvisorChange}
          size="small"
          value={
            advisorOptions.find((option) => option.value === selectedAdvisor) || undefined
          }
          width="auto"
          minWidth="111px"
          disabled={loading}
        >
          <Select.Menu width="160px">
            <Select.Search
              placeholder="Search"
              onChange={(value) => updateLocalSearch("advisor", value)}
              label=""
            />
            {filteredAdvisor.map((option) => (
              <Select.Item key={option.value} value={option.value} label={option.label}>
                {option.label}
              </Select.Item>
            ))}
          </Select.Menu>
        </Select>
        
        <Select
          key={`last-update-filter-${filtersKey}`}
          label="Last Update"
          placeholder="Last Update"
          onChange={handleLastUpdatePresetChange}
          size="small"
          value={
            lastUpdateOptions.find((option) => option.value === selectedLastUpdatePreset) || undefined
          }
          width="auto"
          minWidth="141px"
          disabled={loading}
        >
          <Select.Menu width="160px">
            <Select.Search
              placeholder="Search"
              onChange={(value) => updateLocalSearch("lastUpdate", value)}
              label=""
            />
            {filteredLastUpdate.map((option) => (
              <Select.Item
                key={option.value}
                value={option.value}
                label={option.label}
              >
                {option.label}
              </Select.Item>
            ))}
          </Select.Menu>
        </Select>
        
        <Select
          key={`vehicle-filter-${filtersKey}`}
          label="Vehicle"
          placeholder="Vehicle"
          onChange={handleVehicleChange}
          size="small"
          value={
            vehicleOptions.find((option) => option.value === selectedVehicle) || undefined
          }
          width="auto"
          minWidth="108px"
          disabled={loading}
        >
          <Select.Menu width="160px">
            <Select.Search
              placeholder="Search"
              onChange={(value) => updateLocalSearch("vehicle", value)}
              label=""
            />
            {filteredVehicle.map((vehicle) => (
              <Select.Item
                key={vehicle.value}
                value={vehicle.value}
                label={vehicle.label}
              >
                {vehicle.label}
              </Select.Item>
            ))}
          </Select.Menu>
        </Select>
        
        {hasActiveFilters && (
          <Button
            icon=""
            iconLeft=""
            iconRight=""
            label="Clear Filters"
            size="medium"
            type="secondary"
            onClick={handleClearFiltersWithKeys}
            disabled={loading}
          />
        )}
      </FiltersColumn>
      <TableSwitcherColumn>
        <TableSwitcherPlaceholder>
          <SwitchMode mode={mode} onModeChange={setMode} />
        </TableSwitcherPlaceholder>
      </TableSwitcherColumn>
    </FiltersContainerWrapper>
  );
});

export default FiltersContainer; 